import { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Spin, message, Tag } from 'antd';
import {
  CheckCircleOutlined,
  ToolOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { dashboardApi } from '@/services/api';
import type {
  DashboardStats,
  AftersalesServiceType,
  AftersalesWorkOrderStatus,
} from '@/types';

const SERVICE_TYPE_LABEL: Record<AftersalesServiceType, string> = {
  software: '软件故障',
  hardware: '硬件故障',
  maintenance: '售后维保',
};

const WORK_ORDER_STATUS_LABEL: Record<AftersalesWorkOrderStatus, string> = {
  created: '待处理',
  pending_confirmation: '待客户确认',
  closed: '已完成',
  rejected: '已退回',
};

const WORK_ORDER_STATUS_COLOR: Record<AftersalesWorkOrderStatus, string> = {
  created: 'default',
  pending_confirmation: 'processing',
  closed: 'success',
  rejected: 'warning',
};

function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const data = await dashboardApi.getStats();
      setStats(data);
    } catch (error: any) {
      message.error(error.message || '加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="售后工单总数"
              value={stats?.totalAftersales || 0}
              prefix={<ToolOutlined />}
              valueStyle={{ color: '#165DFF' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="待客户确认"
              value={stats?.pendingConfirmation || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="已完成工单"
              value={stats?.closedAftersales || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="已退回工单"
              value={stats?.rejectedAftersales || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      <Card title="最近售后工单">
        <Table
          columns={[
            {
              title: '工单号',
              dataIndex: 'serialNumber',
              key: 'serialNumber',
              render: (sn: string) => (
                <a
                  style={{ fontFamily: 'monospace', color: '#165DFF' }}
                  onClick={() => navigate(`/admin/aftersales/${sn}`)}
                >
                  {sn}
                </a>
              ),
            },
            { title: '公司名称', dataIndex: 'companyName', key: 'companyName' },
            {
              title: '服务类型',
              dataIndex: 'serviceType',
              key: 'serviceType',
              render: (type: AftersalesServiceType) =>
                SERVICE_TYPE_LABEL[type] || type,
            },
            { title: '处理人', dataIndex: 'technicianName', key: 'technicianName' },
            {
              title: '状态',
              dataIndex: 'workOrderStatus',
              key: 'workOrderStatus',
              render: (status: AftersalesWorkOrderStatus) => (
                <Tag color={WORK_ORDER_STATUS_COLOR[status]}>
                  {WORK_ORDER_STATUS_LABEL[status]}
                </Tag>
              ),
            },
            {
              title: '创建时间',
              dataIndex: 'createdAt',
              key: 'createdAt',
              render: (date: string) => new Date(date).toLocaleString('zh-CN'),
            },
          ]}
          dataSource={stats?.recentAftersales || []}
          rowKey="serialNumber"
          pagination={false}
        />
      </Card>
    </div>
  );
}

export default DashboardPage;
